import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { ArrowTopRightOnSquareIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { WorkflowsService, type WorkflowRead, type WorkflowRunRead } from '../../client'
import { apiErrorMessage } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { STATUS_LABEL, STATUS_TONE, duration } from '../../lib/schedules'
import Spinner from '../chat/Spinner'
import Modal from '../ui/Modal'

const STEP_TONE: Record<string, string> = {
  done: 'text-emerald-700',
  failed: 'text-red-600',
  skipped: 'text-stone-400',
  running: 'text-purple-700',
  pending: 'text-stone-400',
}

type Props = {
  workflow: WorkflowRead
  brandId: number
  onClose: () => void
}

export default function RunLogDialog({ workflow, brandId, onClose }: Props) {
  const [runs, setRuns] = useState<WorkflowRunRead[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  useEffect(() => {
    WorkflowsService.listRuns(workflow.id, brandId)
      .then((list) => setRuns(list.runs))
      .catch((err: unknown) => {
        setRuns([])
        setError(apiErrorMessage(err))
      })
  }, [workflow.id, brandId])

  return (
    <Modal title={`Runs of ${workflow.name}`} width="max-w-2xl" onClose={onClose}>
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {runs === null && (
        <div className="flex justify-center py-10">
          <Spinner className="size-5 border-purple-200 border-t-purple-700" />
        </div>
      )}
      {runs?.length === 0 && !error && (
        <p className="py-10 text-center text-sm text-stone-500">No runs yet. Run it now or give it a schedule.</p>
      )}
      <ul className="space-y-2">
        {runs?.map((run) => {
          const expanded = open === run.id
          const took = duration(run.started_at, run.finished_at)
          const inputs = Object.entries(run.inputs).filter(([, value]) => value.trim())
          return (
            <li key={run.id} className="rounded-lg border border-stone-200">
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setOpen(expanded ? null : run.id)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
              >
                <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium ${STATUS_TONE[run.status]}`}>
                  {STATUS_LABEL[run.status]}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-stone-800">{formatDateTime(run.started_at)}</span>
                  <span className="block truncate text-xs text-stone-500">
                    {run.trigger === 'schedule' ? 'Scheduled' : 'Run by hand'}
                    {took && ` · took ${took}`}
                    {run.status !== 'skipped' && ` · ${run.credits_used.toFixed(2)} chat credits`}
                  </span>
                </span>
                <ChevronDownIcon className={`size-4 shrink-0 text-stone-400 transition ${expanded ? 'rotate-180' : ''}`} />
              </button>

              {expanded && (
                <div className="space-y-3 border-t border-stone-100 px-3 py-3">
                  {run.reason && <p className="text-xs text-stone-700">{run.reason}</p>}
                  {inputs.length > 0 && (
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                      {inputs.map(([name, value]) => (
                        <div key={name} className="contents">
                          <dt className="text-stone-500">{workflow.inputs.find((i) => i.name === name)?.label ?? name}</dt>
                          <dd className="truncate text-stone-800">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {run.steps.length > 0 && (
                    <ol className="space-y-1.5">
                      {run.steps.map((step) => (
                        <li key={step.position} className="flex gap-2 text-xs">
                          <span className="w-4 shrink-0 text-center font-semibold text-stone-400">{step.position}</span>
                          <span className="min-w-0 flex-1">
                            <span className="text-stone-800">{step.title}</span>
                            <span className={`ml-1.5 ${STEP_TONE[step.status] ?? 'text-stone-400'}`}>{step.status}</span>
                            {step.summary && <span className="block text-stone-500">{step.summary}</span>}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                  {run.thread_id && (
                    <Link
                      to={`/c/${run.thread_id}`}
                      onClick={onClose}
                      className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900"
                    >
                      Open the chat
                      <ArrowTopRightOnSquareIcon className="size-3.5" />
                    </Link>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </Modal>
  )
}
